import { useToast } from '@airship-platform/airship-patterns';
import {
  FlexBox,
  Loader,
  PhoneWithOptInInput,
  SMSPhoneOptInSchema,
} from '@airship-platform/jam-web/components';
import { PlusIcon } from '@airship-platform/jam-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import useUserManagement from '../../../data-access/hooks/useUserManagement';
import ROUTE_PATHS from '../../../routes/paths';
import OnboardingWrapper from './OnboardingWrapper';

const formSchema = z
  .object({
    firstName: z.string().min(2, 'First Name is required').max(50),
    lastName: z.string().min(2, 'Last Name is required').max(50),
    title: z.string().min(2).max(50).optional(),
  })
  .merge(SMSPhoneOptInSchema);

type FormInputs = z.infer<typeof formSchema>;

type PersonalDetailsProps = {
  useUserManagement: ReturnType<typeof useUserManagement>;
};

const PersonalDetails: React.FC<PersonalDetailsProps> = ({
  useUserManagement,
}) => {
  const navigate = useNavigate();

  const { showToast } = useToast();
  const { updateUser, uploadProfileAvatar, isLoading, isFetching, user } =
    useUserManagement;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (
        (file.type === 'image/jpeg' ||
          file.type === 'image/png' ||
          file.type === 'image/gif') &&
        file.size <= 10 * 1024 * 1024
      ) {
        setSelectedFile(file);
        setPreview(URL.createObjectURL(file));
      } else if (file.size > 10 * 1024 * 1024) {
        showToast('File size must be less than 10 MB.', 'warning');
      } else {
        showToast('Please upload a JPEG, PNG, or GIF file.', 'warning');
      }
    }
  };

  const handleFileDelete = () => {
    setSelectedFile(null);
    setPreview(null);
  };

  const profileForm = useForm<FormInputs>({
    values: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      title: user?.title || '',
      phone: user?.phone || '',
      smsOptInConsent: user?.smsOptInConsent?.consented || false,
    },
    mode: 'onSubmit',
    resolver: zodResolver(formSchema),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = profileForm;

  const handleSaveAndExit = async (data: FormInputs) => {
    if (!user) {
      return;
    }
    if (selectedFile) {
      await uploadProfileAvatar(user.userId, selectedFile).then((response) => {
        if (response.success === false) return;
      });
    }
    await updateUser(data).then((response) => {
      if (response.success === false) return;
      navigate(ROUTE_PATHS.HOME);
    });
  };

  const handleNextStep = async (data: FormInputs) => {
    if (!user) {
      return;
    }
    if (selectedFile) {
      await uploadProfileAvatar(user.userId, selectedFile).then((response) => {
        if (response.success === false) return;
      });
    }
    await updateUser(data).then((response) => {
      if (response.success === false) return;
      navigate(ROUTE_PATHS.onboardingStep('complete'));
    });
  };

  return (
    <OnboardingWrapper
      onSaveClick={{ onClick: handleSubmit(handleSaveAndExit) }}
      onNextClick={{
        onClick: handleSubmit(handleNextStep),
        isLoading: isLoading,
      }}
      onPreviousClick={() =>
        navigate(ROUTE_PATHS.onboardingStep('company-details'))
      }
      buttonLabel={'Save & exit setup'}
    >
      <FlexBox
        flexDirection={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        gap={2}
      >
        <Typography variant={'h2'}>
          Let's make sure your personal info is correct
        </Typography>
        <FormProvider {...profileForm}>
          <Box
            component={'form'}
            noValidate
            autoComplete="off"
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <FlexBox gap={1}>
              <Controller
                name={'firstName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={'First Name'}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message}
                    fullWidth
                  />
                )}
              />
              <Controller
                name={'lastName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={'Last Name'}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message}
                  />
                )}
              />
            </FlexBox>
            <Controller
              name={'title'}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={'Title'}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  fullWidth
                />
              )}
            />
            <PhoneWithOptInInput />
          </Box>
        </FormProvider>
      </FlexBox>
      <FlexBox flexDirection={'column'} alignSelf={'flex-start'} gap={2}>
        <input
          accept={'image/jpeg,image/png,image/gif'}
          style={{ display: 'none' }}
          id={'file-upload'}
          ref={fileInputRef}
          type={'file'}
          onChange={handleFileChange}
        />
        {!preview && (
          <>
            <Typography variant={'body3'}>
              {'Profile photo (optional)'}
            </Typography>
            <label htmlFor={'file-upload'}>
              <Button
                variant={'outlined'}
                color={'gray'}
                size={'medium'}
                onClick={handleButtonClick}
                startIcon={<PlusIcon />}
              >
                Add Image
              </Button>
            </label>
          </>
        )}
        {preview && (
          <FlexBox flexDirection={'column'} alignItems={'flex-start'} gap={1}>
            <Avatar
              src={preview}
              alt={'Preview'}
              style={{ maxWidth: '300px', maxHeight: '300px' }}
            />
            <FlexBox gap={1}>
              <Link color={'secondary'} onClick={handleButtonClick}>
                Replace
              </Link>
              <Link color={'secondary'} onClick={handleFileDelete}>
                Remove
              </Link>
            </FlexBox>
          </FlexBox>
        )}
      </FlexBox>
      <Loader isLoading={isFetching} />
    </OnboardingWrapper>
  );
};

export default PersonalDetails;
